import React, { useState, memo, useCallback, useMemo, useRef } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  PersonAdd,
} from "@mui/icons-material";
import { debounce } from "lodash";
import { useAuth } from "../context/EnterpriseAuthContext";
import { supabase } from "../lib/supabase";
import MarketingSiteHeader from "./MarketingSiteHeader";

const Login = memo(() => {
  const { state, actions } = useAuth();
  const [isSignup, setIsSignup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    username: "", // Keep for signup only
  });
  const [formErrors, setFormErrors] = useState({});
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetEmailError, setResetEmailError] = useState("");
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [detectedAccount, setDetectedAccount] = useState(null);
  const [isCheckingDomain, setIsCheckingDomain] = useState(false);
  const [joinRequestSent, setJoinRequestSent] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);

  // Debounced domain check function
  const checkDomain = useMemo(
    () =>
      debounce(async (email) => {
        if (!/\S+@\S+\.\S+/.test(email)) {
          setDetectedAccount(null);
          return;
        }
        setIsCheckingDomain(true);
        try {
          const { data, error } = await supabase.rpc("get_account_by_domain", {
            p_email: email,
          });

          if (error) throw error;

          if (data && data.length > 0) {
            setDetectedAccount(data[0]);
          } else {
            setDetectedAccount(null);
          }
        } catch (error) {
          console.error("Error checking domain:", error);
          setDetectedAccount(null);
        } finally {
          setIsCheckingDomain(false);
        }
      }, 500), // 500ms debounce delay
    []
  );

  // Debounced validation function
  const debouncedValidation = useMemo(
    () =>
      debounce((fieldName, value, isSignup) => {
        const errors = {};

        if (fieldName === "email") {
          return;
        } else if (fieldName === "username" && isSignup) {
          if (!value.trim()) {
            errors.username = "Username is required";
          } else if (value.length < 3) {
            errors.username = "Username must be at least 3 characters";
          }
        } else if (fieldName === "password") {
          if (!value) {
            errors.password = "Password is required";
          } else if (value.length < 6) {
            errors.password = "Password must be at least 6 characters";
          }
        } else if (fieldName === "confirmPassword" && isSignup) {
          const currentPassword = formData.password;
          if (!value) {
            errors.confirmPassword = "Please confirm your password";
          } else if (currentPassword !== value) {
            errors.confirmPassword = "Passwords do not match";
          }
        }

        // Update errors for this specific field
        setFormErrors((prev) => ({ ...prev, ...errors }));
      }, 300), // 300ms debounce delay
    [formData.password]
  );

  const handleInputChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      setFormData((prev) => ({ ...prev, [name]: value }));

      // Clear specific field error when user starts typing
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
      if (name === "email") {
        setSignupSuccess(null);
      }

      // If the email field is changed during signup, check the domain
      if (name === "email" && isSignup) {
        setJoinRequestSent(false); // Reset on email change
        checkDomain(value);
      }

      // Trigger debounced validation
      debouncedValidation(name, value, isSignup);
    },
    [debouncedValidation, isSignup, checkDomain]
  );

  const handleInputBlur = useCallback((event) => {
    const { name, value } = event.target;

    if (name !== "email") {
      return;
    }

    if (!value.trim()) {
      setFormErrors((prev) => ({ ...prev, email: "Email is required" }));
      return;
    }

    if (!/\S+@\S+\.\S+/.test(value)) {
      setFormErrors((prev) => ({
        ...prev,
        email: "Please enter a valid email address",
      }));
    }
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (isSignup && !formData.username.trim()) {
      errors.username = "Username is required";
    } else if (isSignup && formData.username.length < 3) {
      errors.username = "Username must be at least 3 characters";
    }

    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    if (isSignup && !formData.confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (isSignup && formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    return errors;
  }, [
    isSignup,
    formData.email,
    formData.password,
    formData.confirmPassword,
    formData.username,
  ]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      if (submitInFlightRef.current || signupSuccess) {
        return;
      }

      actions.clearError();
      setSignupSuccess(null);

      const errors = validateForm();
      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }

      try {
        submitInFlightRef.current = true;
        setIsSubmitting(true);
        if (isSignup) {
          // If the domain is new, the account name is derived from the username
          const accountName = !detectedAccount ? `${formData.username}'s Team` : null;
          const result = await actions.signup(
            formData.email,
            formData.password,
            formData.username,
            accountName,
            window.location.origin, // Add the redirect base URL
          );
          if (result.success) {
            setSignupSuccess({
              email: formData.email,
              requiresEmailVerification: result.requiresEmailVerification !== false,
            });
          }
        } else {
          await actions.login(formData.email, formData.password);
        }
        // On success, the onAuthStateChange listener in AuthContext will trigger a state
        // update, and the user will be redirected automatically by ProtectedRoute or App routing.
        // Don't navigate immediately - let the auth context finish processing first.
      } catch (error) {
        // The error is already set in the AuthContext, so we just need to log it
        console.error("Login/Signup failed:", error.message);
        // We could add form-specific error handling here if needed
      } finally {
        submitInFlightRef.current = false;
        setIsSubmitting(false);
      }
    },
    [actions, formData, isSignup, validateForm, detectedAccount, signupSuccess]
  );

  const handleRequestToJoin = async () => {
    if (!state.user || !detectedAccount) return;

    try {
      const { error } = await supabase
        .from("join_requests")
        .insert({
          user_id: state.user.id,
          account_id: detectedAccount.id,
        });

      if (error) throw error;

      setJoinRequestSent(true);
    } catch (error) {
      console.error("Error sending join request:", error);
      actions.clearError(); // Clear previous errors
      // Use the context to show the error
      actions.setError(error.message || "Could not send join request.");
    }
  };

  const toggleMode = useCallback(() => {
    setIsSignup(!isSignup);
    setFormData({
      email: "",
      password: "",
      confirmPassword: "",
      username: "",
    });
    setFormErrors({});
    actions.clearError();
    setDetectedAccount(null);
    setJoinRequestSent(false);
    setSignupSuccess(null);
  }, [actions, isSignup]);

  const handleForgotPassword = useCallback(async () => {
    if (!resetEmail.trim()) {
      setResetEmailError("Email is required");
      return;
    }

    if (!/\S+@\S+\.\S+/.test(resetEmail)) {
      setResetEmailError("Please enter a valid email address");
      return;
    }

    try {
      const result = await actions.resetPassword(resetEmail);
      if (result.success) {
        setResetEmailSent(true);
        setResetEmailError("");
      } else {
        setResetEmailError(result.error || "Failed to send reset email");
      }
    } catch (error) {
      setResetEmailError("Failed to send reset email");
    }
  }, [actions, resetEmail]);

  const handleCloseForgotPassword = useCallback(() => {
    setShowForgotPassword(false);
    setResetEmail("");
    setResetEmailError("");
    setResetEmailSent(false);
    actions.clearError();
  }, [actions]);

  const getPasswordStrength = useMemo(
    () => (password) => {
      if (!password) return { strength: 0, text: "" };

      let strength = 0;
      if (password.length >= 6) strength++;
      if (password.length >= 8) strength++;
      if (/[A-Z]/.test(password)) strength++;
      if (/[a-z]/.test(password)) strength++;
      if (/[0-9]/.test(password)) strength++;
      if (/[^A-Za-z0-9]/.test(password)) strength++;

      const strengthText = [
        "Very Weak",
        "Weak",
        "Fair",
        "Good",
        "Strong",
        "Very Strong",
      ];
      const strengthColor = [
        "error",
        "error",
        "warning",
        "info",
        "success",
        "success",
      ];

      return {
        strength: Math.min(strength, 5),
        text: strengthText[Math.min(strength, 5)],
        color: strengthColor[Math.min(strength, 5)],
      };
    },
    []
  );

  const passwordStrength = getPasswordStrength(formData.password);
  const isSubmitDisabled = state.loading || isSubmitting || (isSignup && !!signupSuccess);
  const isBusy = state.loading || isSubmitting;

  return (
    <Box
      sx={{
        bgcolor: "background.default",
        color: "text.primary",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <MarketingSiteHeader />
      <Box
        component="main"
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: { xs: 3, sm: 5 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 420 }}>
          <Box
            sx={{
              p: { xs: 3, sm: 4 },
              bgcolor: "background.paper",
              border: 1,
              borderColor: "divider",
              borderRadius: "var(--q-radius-card, 16px)",
              boxShadow: "var(--q-shadow-md)",
            }}
          >
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Link href="/" aria-label="Quill AI home" sx={{ display: "inline-block", mb: 1.5 }}>
                <Box
                  component="img"
                  src="/QuillAI-Logo.png"
                  alt="Quill AI Logo"
                  sx={{ height: { xs: 96, sm: 120 }, maxWidth: "100%", display: "block" }}
                />
              </Link>
              <Typography variant="h4" component="h1" gutterBottom>
                {isSignup ? "Create your account" : "Welcome back"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isSignup
                  ? "Turn meetings into requirements and documents."
                  : "Sign in to your Quill AI account"}
              </Typography>
            </Box>

            <Box
              role="tablist"
              aria-label="Sign in or create an account"
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                p: 0.5,
                mb: 2.5,
                borderRadius: "var(--q-radius-lg, 10px)",
                bgcolor: "var(--q-subtle)",
              }}
            >
              {[
                { key: false, label: "Sign in" },
                { key: true, label: "Create account" },
              ].map((mode) => {
                const selected = isSignup === mode.key;
                return (
                  <Box
                    key={mode.label}
                    component="button"
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    onClick={() => {
                      if (!selected) toggleMode();
                    }}
                    sx={{
                      all: "unset",
                      cursor: "pointer",
                      textAlign: "center",
                      py: 0.875,
                      borderRadius: "var(--q-radius-md, 8px)",
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      color: selected ? "text.primary" : "text.secondary",
                      bgcolor: selected ? "background.paper" : "transparent",
                      boxShadow: selected ? "var(--q-shadow-sm)" : "none",
                      "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 1 },
                    }}
                  >
                    {mode.label}
                  </Box>
                );
              })}
            </Box>

            {state.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {state.error}
              </Alert>
            )}

            {signupSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Check your email to verify your account. We sent a confirmation
                link to <strong>{signupSuccess.email}</strong>.
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: "grid", gap: 2 }}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                error={!!formErrors.email}
                helperText={formErrors.email}
                required
                autoComplete="email"
                autoFocus
              />

              {isSignup && (isCheckingDomain || formData.email) && (
                <Box sx={{ mt: -1 }} aria-live="polite">
                  {isCheckingDomain && (
                    <Typography variant="caption" color="text.secondary">
                      Checking your organisation...
                    </Typography>
                  )}
                  {!isCheckingDomain && detectedAccount && (
                    <Alert severity="info" sx={{ py: 0.25 }}>
                      An account for <strong>{detectedAccount.name}</strong> already exists.
                      You can request to join after signing up.
                    </Alert>
                  )}
                  {!isCheckingDomain && formData.email && !detectedAccount && /\S+@\S+\.\S+/.test(formData.email) && (
                    <Alert severity="success" sx={{ py: 0.25 }}>
                      Looks like a new workspace. You will be its admin.
                    </Alert>
                  )}
                </Box>
              )}

              {isSignup && state.isAuthenticated && detectedAccount && !joinRequestSent && (
                <Button fullWidth variant="outlined" onClick={handleRequestToJoin}>
                  Request to join {detectedAccount.name}
                </Button>
              )}

              {isSignup && joinRequestSent && (
                <Alert severity="success">
                  Your request has been sent to the admin of {detectedAccount.name}.
                </Alert>
              )}

              {isSignup && (
                <TextField
                  fullWidth
                  label="Username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  error={!!formErrors.username}
                  helperText={formErrors.username}
                  required
                  autoComplete="username"
                />
              )}

              <Box>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleInputChange}
                  error={!!formErrors.password}
                  helperText={formErrors.password}
                  required
                  autoComplete={isSignup ? "new-password" : "current-password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {!isSignup && (
                  <Box sx={{ textAlign: "right", mt: 0.75 }}>
                    <Link
                      component="button"
                      variant="body2"
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowForgotPassword(true);
                      }}
                      underline="hover"
                      sx={{ fontWeight: 500 }}
                    >
                      Forgot password?
                    </Link>
                  </Box>
                )}
                {isSignup && formData.password && (
                  <Box sx={{ mt: 1 }} aria-live="polite">
                    <LinearProgress
                      variant="determinate"
                      value={(passwordStrength.strength / 5) * 100}
                      color={passwordStrength.color}
                      aria-label="Password strength"
                      sx={{ height: 4, borderRadius: 2 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Password strength: {passwordStrength.text}
                    </Typography>
                  </Box>
                )}
              </Box>

              {isSignup && (
                <TextField
                  fullWidth
                  label="Confirm password"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  error={!!formErrors.confirmPassword}
                  helperText={formErrors.confirmPassword}
                  required
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                        >
                          {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                disabled={isSubmitDisabled}
                startIcon={
                  isBusy ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : isSignup ? (
                    <PersonAdd />
                  ) : (
                    <LoginIcon />
                  )
                }
                sx={{ mt: 0.5, minHeight: 44 }}
              >
                {isBusy
                  ? "Please wait..."
                  : signupSuccess
                  ? "Verification email sent"
                  : isSignup
                  ? "Create account"
                  : "Sign in"}
              </Button>

              <Divider />

              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
                {isSignup ? "Already have an account?" : "New to Quill AI?"}{" "}
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={toggleMode}
                  underline="hover"
                  sx={{ fontWeight: 600, verticalAlign: "baseline" }}
                >
                  {isSignup ? "Sign in" : "Create an account"}
                </Link>
              </Typography>
            </Box>
          </Box>

          {/* Forgot Password Dialog */}
          <Dialog
            open={showForgotPassword}
            onClose={handleCloseForgotPassword}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>Reset your password</DialogTitle>
            <DialogContent>
              {resetEmailSent ? (
                <Box sx={{ pt: 1 }}>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Password reset email sent.
                  </Alert>
                  <Typography variant="body2" color="text.secondary">
                    We sent a reset link to <strong>{resetEmail}</strong>. Follow the
                    instructions in the email to choose a new password.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Nothing arrived? Check your spam folder or try another address.
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ pt: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Enter your email address and we will send you a link to reset your password.
                  </Typography>

                  {state.error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {state.error}
                    </Alert>
                  )}

                  <TextField
                    fullWidth
                    label="Email address"
                    type="email"
                    value={resetEmail}
                    onChange={(e) => {
                      setResetEmail(e.target.value);
                      setResetEmailError("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleForgotPassword();
                      }
                    }}
                    error={!!resetEmailError}
                    helperText={resetEmailError}
                    required
                    autoComplete="email"
                    autoFocus
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseForgotPassword}>
                {resetEmailSent ? "Close" : "Cancel"}
              </Button>
              {!resetEmailSent && (
                <Button
                  onClick={handleForgotPassword}
                  variant="contained"
                  disabled={state.loading}
                >
                  {state.loading ? "Sending..." : "Send reset link"}
                </Button>
              )}
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </Box>
  );
});

Login.displayName = "Login";

export default Login;
